import React, { useState, useEffect, useContext, useCallback } from "react";
import Select from "react-select";
import axios from "axios";
import { URL, getToken, getLocalDateTime } from "../../utils";
import { UserContext } from "../../contexts/UserContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ErrorModal from "../../components/ErrorModal";
import NumberInput from "../../components/NumberInput";
import ConditionalRender from "../../components/ConditionalRender";

// Fonction utilitaire pour formater les types de mouvement
const formatMovementType = (type) => {
  switch (type) {
    case "approvisionnement_entrepot":
      return "Approvisionnement entrepôt";
    case "approvisionnement_store":
      return "Approvisionnement store";
    case "retour_client":
      return "Retour client";
    case "retour_event":
      return "Retour événement";
    case "autre_type":
      return "Autre";
    default:
      return type;
  }
};

// Composants d'icônes optimisés
const MovementIcons = {
  retour: React.memo(() => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      className="inline mr-1"
    >
      <path
        d="M12 4V8"
        stroke="#FF9800"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M12 16V20"
        stroke="#FF9800"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M8 12H4"
        stroke="#FF9800"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M20 12H16"
        stroke="#FF9800"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M7.75732 7.75735L10.5858 10.5858"
        stroke="#FF9800"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M13.4142 13.4142L16.2426 16.2426"
        stroke="#FF9800"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M7.75732 16.2426L10.5858 13.4142"
        stroke="#FF9800"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M13.4142 10.5858L16.2426 7.75736"
        stroke="#FF9800"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )),
  approvissionnement: React.memo(() => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      className="inline mr-1"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M12 21a9 9 0 1 1 0-18c1.052 0 2.062.18 3 .512M7 9.577l3.923 3.923 8.5-8.5M17 14v6m-3-3h6"
      />
    </svg>
  )),
  autre: React.memo(() => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      className="inline mr-1"
    >
      <path d="M9 22H15V20H9V22Z" fill="#2196F3" />
      <path d="M19 13H5V11H19V13Z" fill="#2196F3" />
      <path d="M12 5V19H10V5H12Z" fill="#2196F3" />
      <path d="M5 11L12 4L19 11H5Z" fill="#2196F3" />
    </svg>
  )),
};

const StockEnterflowApp = () => {
  const [state, setState] = useState({
    movements: [],
    loading: true,
    sellPoints: [],
    articles: [],
    isSubmitting: false,
    clients: [], // Liste combinée de tous les clients
  });

  const { user } = useContext(UserContext);
  const token = getToken();
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [currentMovement, setCurrentMovement] = useState(null);

  // Configuration des options
  const options = React.useMemo(
    () => ({
      movementTypes: [
        { value: "retour_client", label: "Retour de produit par un client" },
        { value: "retour_event", label: "Retour de produit d'un evenement" },
        {
          value: "approvisionnement_entrepot",
          label: "approvisionnement par l'entrepot",
        },
        {
          value: "approvisionnement_store",
          label: "approvisionnement par un autre store",
        },
        { value: "autre_type", label: "Autres types de retour" },
      ],
      units: [{ value: "unite", label: "Unité" }],
    }),
    []
  );

  const [formData, setFormData] = useState({
    stock_item: null,
    quantite: "",
    unite: "unite",
    magasin: user.is_caissier ? user.magasin_id : null,
    type: "retour_client",
    receiving: user.is_caissier ? user.magasin_id : null,
    commentaire: "",
    name_event: "",
    client: "",
    magasin_sender: null,
  });

  // Récupération des articles
  const fetchArticles = useCallback(
    async (magasinId) => {
      try {
        const response = await axios.get(
          `${URL}Magasins/articles-magasin/${magasinId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const articlesData = response.data.stock_items.map((article) => ({
          value: article.id,
          label: article.nom,
          quantite: article.quantite,
          stock: article.quantite,
        }));

        setState((prev) => ({ ...prev, articles: articlesData }));
      } catch (error) {
        console.error("Error:", error);
        toast.error("Erreur lors du chargement des articles");
      }
    },
    [token]
  );

  // Récupération des clients (particuliers et professionnels)
  const fetchClients = useCallback(async () => {
    try {
      // Récupération des particuliers et des professionnels en parallèle
      const [particuliersResponse, professionalsResponse] = await Promise.all([
        axios.get(URL + "Clients/clients-particuliers-list", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(URL + "Clients/clients-professionnels-list", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      // Formatage des particuliers
      const particuliers = particuliersResponse.data.map((client) => ({
        value: client.id,
        label: `${client.nom} ${client.prenom}`,
        type: "particulier",
      }));

      // Formatage des professionnels
      const professionals = professionalsResponse.data.map((client) => ({
        value: client.id,
        label: client.nom_entreprise,
        type: "professionnel",
      }));

      // Combiner les deux types de clients
      const allClients = [...particuliers, ...professionals];

      // Mise à jour de l'état
      setState((prev) => ({ ...prev, clients: allClients }));
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Erreur lors du chargement des clients");
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    // Préparer les headers une seule fois
    const commonHeaders = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    try {
      // Mettre à jour l'état de chargement une seule fois
      setState((prev) => ({ ...prev, loading: true }));

      // Préparer les requêtes en parallèle
      const requests = [
        axios.get(`${URL}Magasins/magasins-list`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        user.is_caissier
          ? axios.get(
              `${URL}Declarations/entrees/liste-entree/${user.magasin_id}`,
              {
                headers: commonHeaders,
              }
            )
          : axios.get(`${URL}Declarations/entrees/liste-entree`, {
              headers: commonHeaders,
            }),
      ];

      // Exécuter toutes les requêtes en parallèle
      const [sellPointsResponse, movementsResponse] = await Promise.all(
        requests
      );

      // Transformer les données des magasins
      const sellPts = sellPointsResponse.data.map((store) => ({
        value: store.id,
        label: store.nom,
      }));

      // Mise à jour unique de l'état
      setState((prev) => ({
        ...prev,
        sellPoints: sellPts,
        loading: false,
        // Ajouter les mouvements si nécessaire
        ...(movementsResponse.data && { movements: movementsResponse.data }),
      }));

      // Charger les articles si c'est un caissier
      if (user.is_caissier) {
        fetchArticles(user.magasin_id);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erreur lors du chargement des données");
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [token, user, fetchArticles]);

  useEffect(() => {
    fetchData();
    fetchClients(); // Charger les clients au chargement du composant
  }, [fetchData, fetchClients]);

  // Gestion des changements
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseFloat(value) || "" : value,
    }));
  };

  // Gestion des changements pour le composant NumberInput
  const handleNumberChange = (name, value) => {
    // Vérifier si la valeur est définie avant de l'utiliser
    setFormData((prev) => ({
      ...prev,
      [name]: value === undefined ? "" : value,
    }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption?.value || null,
      // Si on change de magasin receveur, réinitialiser l'article et la quantité
      ...(name === "receiving" && { stock_item: null, quantite: "" }),
      // Si on change de type, réinitialiser les champs spécifiques
      ...(name === "type" && {
        name_event: "",
        client: "",
        magasin_sender: null,
      }),
    }));

    if (name === "receiving" && selectedOption) {
      fetchArticles(selectedOption.value);
    }
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setState((prev) => ({ ...prev, isSubmitting: true }));

    // Convertir les champs vides en null
    const dataToSubmit = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => {
        // Si la valeur est une chaîne vide, la convertir en null
        return [key, value === "" ? null : value];
      })
    );

    try {
      const response = await axios.post(
        `${URL}Declarations/ajouter-stock/${formData.receiving}`,
        dataToSubmit,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Mouvement enregistré");
      if ([200, 201].includes(response.status)) {
        await fetchData();
        resetForm();
      }
    } catch (error) {
      console.error("Error:", error);

      if (error.response?.status === 400) {
        setErrors(error.response.data || {});
        setModalOpen(true);
      } else {
        toast.error(
          error.response?.data?.message || "Erreur lors de l'enregistrement"
        );
      }
    } finally {
      setState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const resetForm = () => {
    // Réinitialiser le formulaire avec les valeurs par défaut
    setFormData({
      stock_item: null,
      quantite: "",
      unite: "unite",
      magasin: user.is_caissier ? user.magasin_id : null,
      type: "retour_client", // Valeur par défaut
      receiving: user.is_caissier ? user.magasin_id : null,
      commentaire: "",
      name_event: "",
      client: "",
      magasin_sender: null,
    });

    // Réinitialiser les erreurs
    setErrors({});
  };

  const isFormValid = () => {
    const {
      receiving,
      stock_item,
      quantite,
      unite,
      type,
      commentaire,
      name_event,
      client,
      magasin_sender,
    } = formData;

    // Vérifications communes à tous les types
    const baseConditions =
      receiving && stock_item && quantite > 0 && unite && type && commentaire;

    // Si pas de conditions de base, pas besoin d'aller plus loin
    if (!baseConditions) return false;

    // Vérifications spécifiques selon le type de mouvement
    switch (type) {
      case "retour_client":
        return !!client;
      case "retour_event":
        return !!name_event;
      case "approvisionnement_store":
        return !!magasin_sender;
      case "approvisionnement_entrepot":
      case "autre_type":
        return true;
      default:
        return false;
    }
  };

  if (state.loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  // Display errors if any
  if (Object.keys(errors).length > 0) {
    <div
      className="
    "
    ></div>;
  }

  return (
    <>
      <ErrorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        errors={errors}
      />

      {currentMovement && (
        <div className="fixed top-0 left-0 inset-0 z-50 py-8">
          <button
            className="absolute top-0 left-0 inset-0 bg-black bg-opacity-15 backdrop-blur-sm -z-10"
            onClick={() => {
              setCurrentMovement(null);
            }}
          ></button>
          <div className="mx-auto h-max w-[min(80%,500px)] bg-white p-4 rounded-md shadow-md space-y-4">
            <div className="flex item-center justify-between">
              <span className="flex item-center gap-2">
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 5a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1 2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2ZM3 19v-7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Zm6.01-6a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-10 4a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  {getLocalDateTime(new Date(currentMovement.date_entree))}
                </span>
              </span>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  currentMovement.type === "approvissionnement_entrepot" ||
                  currentMovement.type === "approvissionnement_store"
                    ? "bg-green-100 text-green-800"
                    : currentMovement.type === "retour_client" ||
                      currentMovement.type === "retour_event"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-primary-100 text-primary-800"
                }`}
              >
                {React.createElement(
                  MovementIcons[
                    currentMovement.type === "approvissionnement_entrepot" ||
                    currentMovement.type === "approvissionnement_store"
                      ? "approvissionnement"
                      : currentMovement.type === "retour_client" ||
                        currentMovement.type === "retour_event"
                      ? "retour"
                      : "autre"
                  ]
                )}
                {formatMovementType(currentMovement.type)}
              </span>
            </div>
            <h3>Article: {currentMovement.stock_item_name}</h3>
            <div className="space-y-1">
              <p className="text-lg font-medium">Details</p>
              <ul className="capitalize p-4 rounded-md bg-gray-100 font-light">
                <li>Unité: {currentMovement.unite}</li>
                <li>Type: {currentMovement.type}</li>
                <li>Quantité: {currentMovement.quantite}</li>
                {currentMovement.magasin_name && (
                  <li>{currentMovement.magasin_name}</li>
                )}
              </ul>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setCurrentMovement(null);
                }}
                className="p-2 px-3 rounded-md bg-blue-500 text-white"
              >
                Fermer ❌
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="shadow-md bg-white p-4 sticky top-16 z-10 ">
        <h1 className="text-2xl font-bold text-gray-900">
          Gestion des entree des stocks
        </h1>
        <p className="text-gray-600 mt-1">Suivi des stocks</p>
      </div>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-8 p-4 2xl:p-6">
        {/* Formulaire */}
        <ConditionalRender requiredPermissions="canCreateStockEntry">
          <div className="bg-white shadow rounded-lg overflow-hidden xl:col-span-2">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                Nouvelle entrée de stock
              </h2>
            </div>
            <div className="p-6 space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {!user.is_caissier && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Magasin receveur
                    </label>
                    <Select
                      name="receiving"
                      options={state.sellPoints}
                      onChange={handleSelectChange}
                      value={state.sellPoints.find(
                        (opt) => opt.value === formData.receiving
                      )}
                      placeholder="Sélectionnez un magasin"
                      isClearable
                      required
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Article
                  </label>
                  <Select
                    name="stock_item"
                    options={state.articles}
                    onChange={(option) =>
                      handleSelectChange(option, { name: "stock_item" })
                    }
                    value={state.articles.find(
                      (opt) => opt.value === formData.stock_item
                    )}
                    placeholder={
                      formData.receiving
                        ? "Sélectionnez un article"
                        : "Sélectionnez d'abord un magasin"
                    }
                    isClearable
                    required
                    isDisabled={!formData.receiving}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    getOptionLabel={(option) =>
                      `${option.label} (Stock: ${option.stock || 0})`
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type de mouvement
                  </label>
                  <Select
                    name="type"
                    options={options.movementTypes}
                    onChange={(option) =>
                      handleSelectChange(option, { name: "type" })
                    }
                    value={options.movementTypes.find(
                      (opt) => opt.value === formData.type
                    )}
                    placeholder="Type de mouvement"
                    required
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
                {/* Conditions pour les champs spécifiques selon le type */}
                {formData.type === "approvisionnement_store" &&
                  !user.is_caissier && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Magasin source
                      </label>
                      <Select
                        name="magasin_sender"
                        options={state.sellPoints}
                        onChange={(option) =>
                          handleSelectChange(option, { name: "magasin_sender" })
                        }
                        value={state.sellPoints.find(
                          (opt) => opt.value === formData.magasin_sender
                        )}
                        placeholder="Sélectionnez un magasin source"
                        isClearable
                        required
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                  )}

                {formData.type === "retour_client" && (
                  <div>
                    <label className="block text-sm font-medium text- gray-700 mb-1">
                      Client
                    </label>
                    <Select
                      name="client"
                      options={state.clients}
                      onChange={(selectedOption, { name }) => {
                        handleSelectChange(selectedOption, { name });
                      }}
                      value={state.clients.find(
                        (client) => client.value === formData.client
                      )}
                      placeholder="Sélectionnez un client"
                      required
                      getOptionLabel={(option) =>
                        `${option.label} (${
                          option.type === "particulier"
                            ? "Particulier"
                            : "Professionnel"
                        })`
                      }
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                    <div className="mt-1 text-xs text-gray-500">
                      Liste combinée des particuliers et professionnels
                    </div>
                  </div>
                )}

                {formData.type === "retour_event" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom de l'événement
                    </label>
                    <input
                      type="text"
                      name="name_event"
                      value={formData.name_event}
                      onChange={handleChange}
                      placeholder="Nom de l'événement"
                      required
                      className="mt-1 block w-full shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                )}

                {/* Champ commentaire obligatoire pour tous les types */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commentaire
                  </label>
                  <textarea
                    name="commentaire"
                    value={formData.commentaire}
                    onChange={handleChange}
                    placeholder="Ajoutez un commentaire"
                    required
                    className="mt-1 block w-full shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm border-gray-300 rounded-md"
                    rows="3"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unité
                    </label>
                    <Select
                      name="unite"
                      options={options.units}
                      onChange={(option) =>
                        handleSelectChange(option, { name: "unite" })
                      }
                      value={options.units.find(
                        (opt) => opt.value === formData.unite
                      )}
                      placeholder="Sélectionnez une unité"
                      required
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantité
                    </label>
                    <NumberInput
                      name="quantite"
                      value={formData.quantite || ""}
                      onChange={(value) =>
                        handleNumberChange("quantite", value)
                      }
                      placeholder="Quantité"
                      required
                      className="w-full border p-2.5 rounded"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={!isFormValid() || state.isSubmitting}
                  className={`w-full flex justify-center py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white ${
                    isFormValid()
                      ? "bg-amber-600 hover:bg-amber-700 focus:ring-2 focus:ring-amber-500"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  {state.isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Enregistrement...
                    </>
                  ) : (
                    "Enregistrer"
                  )}
                </button>
              </form>
            </div>
          </div>
        </ConditionalRender>
        {/* Historique */}
        <div className="bg-white shadow rounded-lg overflow-hidden xl:col-span-3">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Historique</h2>
          </div>
          <div className="p-6">
            {state.movements.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="mt-2">Aucun mouvement enregistré</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Article
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Magasin de provenance
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantité
                      </th>
                      <th className="sr-only">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {state.movements.map((movement) => (
                      <tr key={movement.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {getLocalDateTime(new Date(movement.date_entree))}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {movement.stock_item_name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              movement.type === "approvissionnement_entrepot" ||
                              movement.type === "approvissionnement_store"
                                ? "bg-green-100 text-green-800"
                                : movement.type === "retour_client" ||
                                  movement.type === "retour_event"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-primary-100 text-primary-800"
                            }`}
                          >
                            {React.createElement(
                              MovementIcons[
                                movement.type ===
                                  "approvissionnement_entrepot" ||
                                movement.type === "approvissionnement_store"
                                  ? "approvissionnement"
                                  : movement.type === "retour_client" ||
                                    movement.type === "retour_event"
                                  ? "retour"
                                  : "autre"
                              ]
                            )}
                            {formatMovementType(movement.type)}
                          </span>
                        </td>
                        <td
                          className={[
                            "px-4 py-3 whitespace-nowrap text-sm font-medium",
                            movement.magasin_name
                              ? "text-gray-700"
                              : "text-gray-400 italic text-sm",
                          ].join(" ")}
                        >
                          {movement.magasin_name ?? "Non spécifier"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {movement.quantite} {movement.unite}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 flex justify-end items-center">
                          <button
                            onClick={() => {
                              setCurrentMovement(movement);
                            }}
                            className="px-2 py-1 rounded-md shadow-md bg-blue-500 hover:bg-blue-600 text-white"
                          >
                            Plus >
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StockEnterflowApp;
